import { useLayoutEffect, useRef, useState } from 'react';
/** Design chip is 40px tall. 4px keeps the ring shadow off the row. */
const ANCHORED_BAR_HEIGHT_PX = 40;
const ANCHORED_BAR_GAP_PX = 4;
/**
 * useAnchoredSelectionToolbar
 *
 * Drives the two-state floating selection bar (decisions 20–22):
 *
 *   Anchored (not stuck):
 *     The bar sits just above the anchor (first checked row, or the header
 *     row when the column select-all checkbox is the first one checked).
 *     Width is `max-content` (content-sized).
 *     Position is `fixed` so the bar floats over the page scroll.
 *
 *   Stuck:
 *     The anchor has scrolled above `pageHeader.bottom`.
 *     The bar pins to `pageHeader.bottom` (not top: 0).
 *     Width equals `contentWidth` (the listing content column width).
 *
 * The hook does NOT apply colors, borders, or padding — callers toggle CSS
 * classes based on `stuck`.
 *
 * @param anchor      - The first selected row or card DOM element (or null).
 * @param pageHeader  - The sticky workspace header element (or null).
 * @param contentWidth - CSS width string for the full content column (stuck state).
 */
export function useAnchoredSelectionToolbar(anchor, pageHeader, contentWidth = '100%') {
    const [stuck, setStuck] = useState(false);
    const [style, setStyle] = useState({});
    const rafRef = useRef(null);
    useLayoutEffect(() => {
        if (!anchor) {
            setStuck(false);
            setStyle({});
            return undefined;
        }
        const update = () => {
            const anchorRect = anchor.getBoundingClientRect();
            // Clamp to 0 so that if the header element scrolls out of view (non-fixed)
            // the bar still sticks at the viewport top rather than never triggering.
            const rawHeaderBottom = pageHeader ? pageHeader.getBoundingClientRect().bottom : 0;
            const headerBottom = Math.max(0, rawHeaderBottom);
            const barHeight = ANCHORED_BAR_HEIGHT_PX + ANCHORED_BAR_GAP_PX;
            const anchoredTop = anchorRect.top - barHeight;
            const isStuck = anchoredTop <= headerBottom;
            setStuck(isStuck);
            const viewportWidth = window.innerWidth;
            const left = Math.max(0, Math.min(anchorRect.left, viewportWidth));
            const available = Math.max(0, viewportWidth - left);
            if (isStuck) {
                const stuckWidth = contentWidth === '100%' ? available : contentWidth;
                setStyle({
                    position: 'fixed',
                    top: headerBottom,
                    left,
                    width: stuckWidth,
                    maxWidth: available,
                    boxSizing: 'border-box',
                    zIndex: 10
                });
            }
            else {
                setStyle({
                    position: 'fixed',
                    top: anchoredTop,
                    left,
                    width: 'max-content',
                    maxWidth: available,
                    boxSizing: 'border-box',
                    zIndex: 10
                });
            }
        };
        const scheduleUpdate = () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
            rafRef.current = requestAnimationFrame(() => {
                update();
                rafRef.current = null;
            });
        };
        update();
        window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
        window.addEventListener('resize', scheduleUpdate);
        return () => {
            window.removeEventListener('scroll', scheduleUpdate, { capture: true });
            window.removeEventListener('resize', scheduleUpdate);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [anchor, pageHeader, contentWidth]);
    return { stuck, style };
}
